// List filters that survive leaving the list (SEM-6350).
//
// The defect these remove: a filter held in `useState` dies with the component.
// Now that a row's subject is a real address, opening one UNMOUNTS the list —
// the user returns to page 1, unfiltered, at the top. That makes the link
// itself worthless: nobody clicks row 60 twice if finding it again is the cost.
//
// The location is the only place that survives an unmount, so the filter lives
// there. Three things follow for free: browser back/forward work, a filtered
// view is shareable, and a reload restores it.
//
// `replace: true` everywhere: a filter keystroke is not a place the user asked
// to return to. Pushing would make Back walk them through their own typing.
//
// **A URL filter is not an authorization token.** These params only NARROW what
// the server would return; the server always scopes to the caller first. A
// hand-edited param cannot widen a page — see the API-side scope tests.

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

/**
 * One filter value in the query string. The default value is the ABSENT param,
 * so a freshly opened page and the same page after clearing its filter share
 * one canonical URL.
 */
export function useFilterParam(
  paramName: string,
  defaultValue = "",
): readonly [string, (next: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(paramName) ?? defaultValue;

  const setValue = useCallback(
    (next: string): void => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === defaultValue || next === "") params.delete(paramName);
          else params.set(paramName, next);
          return params;
        },
        { replace: true },
      );
    },
    [defaultValue, paramName, setSearchParams],
  );

  return [value, setValue] as const;
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * A search box whose committed term is in the URL.
 *
 * Returns the live input, its setter, and the COMMITTED term.
 *
 * Which one to filter on is not a style choice. A SERVER-side filter takes the
 * committed term — it rides the query key, so a response for an abandoned
 * prefix lands in that prefix's cache entry instead of overwriting the current
 * one. A CLIENT-side narrow over an already-loaded window takes the live input:
 * the debounce is there to keep the URL quiet, and making the user watch their
 * own typing land 300ms late would be a defect, not persistence.
 *
 * The box seeds from the URL once per mount. It does not follow later external
 * changes to the param: the user is typing in it, and a query-string write from
 * elsewhere must not yank the caret's text out from under them.
 */
export function useDebouncedSearchParam(
  paramName: string,
  delayMs: number = SEARCH_DEBOUNCE_MS,
): readonly [string, (next: string) => void, string] {
  const [committed, setCommitted] = useFilterParam(paramName);
  const [input, setInput] = useState(committed);

  useEffect(() => {
    if (input === committed) return;
    const timer = window.setTimeout(() => setCommitted(input), delayMs);
    return (): void => window.clearTimeout(timer);
  }, [committed, delayMs, input, setCommitted]);

  return [input, setInput, committed] as const;
}
